import type { PdfTemplate } from "../types";

/**
 * Advisory Multi-Goal, Multi-Portfolio - Standard PDF Template (English)
 *
 * QP-150 variant of {@link advisoryMultiStandard}. The per-goal section is trimmed to
 * goal-level content (goal info + risk); its bonded `per_portfolio` sub-node holds the portfolio
 * analytics (display, forecast, X-Ray) and renders once per portfolio of each goal. Wealth plan
 * and costs are instance-wide — a trailing global section rendered once at the end. Mirrors the
 * flow template "Advisory Multi-Goal, Multi-Portfolio".
 */
export const advisoryMultiPortfolioStandard: PdfTemplate = {
  key: "advisory-multi-portfolio-standard",
  category: "advisory",
  language: "en_GB",
  name: "Advisory Multi-Goal, Multi-Portfolio - Standard",
  description:
    "Advisory documentation for multi-goal, multi-portfolio advice. Goal info + risk per goal; portfolio analysis (X-Ray) and forecast repeated per portfolio; wealth plan and costs shown once at the end.",
  badges: ["MiFID II", "Multi-Goal", "Multi-Portfolio", "X-Ray"],

  tableStyleType: "alternating",
  tableHeaderBgColor: "#b70404",
  tableHeaderTextColor: "#ffffff",
  tableAlternatingColor: "#eecdcd",

  sections: [
    // === SECTION 1: Cover Page (global) ===
    {
      name: "Cover Page",
      scope: "global",
      order: 0,
      headerShowLine: false,
      footerShowNumbers: false,
      footerShowLine: false,
      pages: [
        {
          name: "Cover",
          order: 0,
          components: [
            { type: "spacer", order: 0, config: { height: 12 } },
            {
              type: "text",
              order: 1,
              config: {
                content: "Investment Advice for\n{{Client.Name}}",
                align: "center",
                fontSize: 30,
                maxWidth: 12,
              },
            },
          ],
        },
      ],
    },

    // === SECTION 2: Global Section (global) ===
    {
      name: "Global Section",
      scope: "global",
      order: 1,
      headerText: "Investment report for {{Client.Name}}",
      headerShowLine: true,
      footerShowNumbers: true,
      footerShowLine: true,
      pages: [
        {
          name: "Client Profile",
          order: 0,
          components: [
            { type: "heading", order: 0, config: { title: "Investment Proposal" } },
            {
              type: "text",
              order: 1,
              columnPosition: "left",
              config: {
                content:
                  "Thank you for your trust. Your advisor is proud to present this investment opportunity to you.",
              },
            },
            {
              type: "advisor_image",
              order: 2,
              columnPosition: "right",
              config: { width: 20, align: "left", shape: "circle" },
            },
            {
              type: "table",
              order: 3,
              config: {
                title: "Client Data",
                description:
                  "Below is a summary of your personal identification and contact information as provided in our advisory session.",
                rows: [
                  { col1: "Name", col2: "{{Client.Name}}" },
                  { col1: "Type", col2: "{{Client.Type}}" },
                  { col1: "Client Segment", col2: "{{Client.Team}}" },
                ],
              },
            },
            {
              type: "client_classification",
              order: 4,
              config: {
                title: "Client Classification",
                description:
                  "Classification according to MiFID II regulations based on the client's knowledge and experience.",
              },
            },
            {
              type: "product_assessment",
              order: 5,
              config: {
                title: "Product Assessment",
                description:
                  "Overview of the client's previous experience with various investment products.",
              },
            },
          ],
        },
      ],
    },

    // === SECTION 3: Goal Section (per_goal — goal-level only) ===
    {
      name: "Goal Section",
      scope: "per_goal",
      order: 2,
      headerText: "Investment report for {{Client.Name}}",
      headerShowLine: true,
      footerShowNumbers: true,
      footerShowLine: true,
      pages: [
        {
          name: "Goal",
          order: 0,
          components: [
            { type: "heading", order: 0, config: { title: "Goal" } },
            {
              type: "goal_info",
              order: 1,
              columnPosition: "left",
              config: {
                title: "Goal Information",
                description: "Details about the investment goal and target.",
              },
            },
            {
              type: "risk_assessment",
              order: 2,
              columnPosition: "right",
              config: {
                title: "Risk Assessment",
                description: "Risk profile assessment based on client answers.",
              },
            },
          ],
        },
      ],
    },

    // === SECTION 4: Portfolio Section (per_portfolio — repeated per portfolio) ===
    {
      name: "Portfolio Section",
      scope: "per_portfolio",
      order: 3,
      headerText: "Investment report for {{Client.Name}}",
      headerShowLine: true,
      footerShowNumbers: true,
      footerShowLine: true,
      pages: [
        {
          name: "Portfolio Analysis",
          order: 0,
          components: [
            { type: "heading", order: 0, config: { title: "Portfolio" } },
            {
              type: "portfolio_display",
              order: 1,
              columnPosition: "left",
              config: {
                title: "Portfolio Display",
                description: "Overview of portfolio holdings.",
                showWeights: true,
                showIsin: true,
                showAmount: true,
              },
            },
            {
              type: "portfolio_forecast",
              order: 2,
              columnPosition: "right",
              config: {
                title: "Portfolio Forecast",
                description: "Expected portfolio development based on Monte Carlo simulations.",
                showChart: true,
                showTable: true,
              },
            },
            // Page break before X-Ray
            { type: "page_break", order: 3, config: {} },
            {
              type: "portfolio_xray",
              order: 4,
              columnPosition: "left",
              config: {
                title: "Equity Style Box",
                description: "Morningstar equity style classification.",
                displayMode: "equity_style",
                showChart: true,
                showTable: false,
              },
            },
            {
              type: "portfolio_xray",
              order: 5,
              columnPosition: "right",
              config: {
                title: "Country Exposure",
                displayMode: "country_exposure",
                showChart: true,
                showTable: true,
                enableFollowupComparison: true,
              },
            },
            {
              type: "portfolio_xray",
              order: 6,
              columnPosition: "left",
              config: {
                title: "Asset Classes",
                description: "Asset allocation with Morningstar X-Ray",
                displayMode: "asset_allocation",
                showChart: true,
                showTable: true,
                enableFollowupComparison: true,
              },
            },
            {
              type: "portfolio_xray",
              order: 7,
              columnPosition: "right",
              config: {
                title: "Regional Exposure",
                displayMode: "regional_exposure",
                showChart: true,
                showTable: true,
                enableFollowupComparison: true,
              },
            },
            {
              type: "asset_allocation",
              order: 8,
              columnPosition: "left",
              config: {
                title: "Asset Allocation",
                description: "Asset allocation with internal asset classes",
                showChart: true,
                showTable: true,
                enableFollowupComparison: true,
              },
            },
            {
              type: "portfolio_xray",
              order: 9,
              columnPosition: "right",
              config: {
                title: "Stock Sector",
                displayMode: "stock_sector",
                showChart: true,
                showTable: true,
                enableFollowupComparison: true,
              },
            },
            {
              type: "portfolio_xray",
              order: 10,
              columnPosition: "right",
              config: {
                title: "Bond Sector",
                displayMode: "bond_sector",
                showChart: true,
                showTable: true,
                enableFollowupComparison: true,
              },
            },
            {
              type: "portfolio_xray",
              order: 11,
              config: {
                title: "Top 10 Holdings",
                displayMode: "top_holdings",
                showChart: true,
                showTable: false,
              },
            },
          ],
        },
      ],
    },

    // === SECTION 5: Wealth Plan & Costs (global — after goals, instance-wide) ===
    // Mirrors the flow's `after_goals` pages: rendered once at the end, after every goal + its
    // portfolios. The builder emits global sections in array order, so placing this after the
    // per_portfolio section lands it at the very end.
    {
      name: "Wealth Plan & Costs",
      scope: "global",
      order: 4,
      headerText: "Investment report for {{Client.Name}}",
      headerShowLine: true,
      footerShowNumbers: true,
      footerShowLine: true,
      pages: [
        {
          name: "Wealth Plan",
          order: 0,
          components: [
            { type: "heading", order: 0, config: { title: "Wealth Plan" } },
            {
              type: "wealth_plan",
              order: 1,
              columnPosition: "left",
              config: {
                title: "Wealth Plan",
                description:
                  "Overview of planned withdrawals and expected development of your total wealth.",
              },
            },
            {
              type: "expected_value",
              order: 2,
              columnPosition: "right",
              config: {
                title: "Expected Value",
                showChart: true,
                showSummaryTable: true,
                description: "Overview of expected portfolio value development over time.",
              },
            },
          ],
        },
        {
          name: "Costs",
          order: 1,
          components: [
            { type: "heading", order: 0, config: { title: "Costs" } },
            {
              type: "cost_chart",
              order: 1,
              columnPosition: "left",
              config: {
                title: "Cost Projection (Rolling)",
                displayMode: "rolling",
                description: "Cost projection over rolling periods.",
              },
            },
            {
              type: "cost_table",
              order: 2,
              columnPosition: "right",
              config: {
                title: "Cost Table",
                description: "Overview of all costs associated with your investment.",
              },
            },
            {
              type: "portfolio_costs",
              order: 3,
              columnPosition: "left",
              config: {
                title: "Advisory Costs",
                description: "Platform and advisory costs for your investment portfolio.",
              },
            },
            {
              type: "product_costs",
              order: 4,
              columnPosition: "right",
              config: {
                title: "Product Costs",
                description: "Ongoing product costs.",
              },
            },
          ],
        },
      ],
    },
  ],
};
